import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useChatSocket, ChatMessagePayload } from '../hooks/useChatSocket';
import { useAuthContext } from './AuthContext';
import axiosInstance from '../services/axiosInstance';
import { ChatActionToast } from '../components/ui/ChatActionToast/ChatActionToast';

export interface ChatConversation {
    id: number;
    otherUser: {
        id: string;
        uid?: string;
        firstName: string;
        lastName: string;
        profilePicture: string | null;
    };
    lastMessage: {
        text: string;
        createdAt: string;
        isRead: boolean;
        isMine: boolean;
    } | null;
    updatedAt: string;
}

interface ChatContextType {
    conversations: ChatConversation[];
    unreadCount: number;
    socketConnected: boolean;
    onlineUsers: Set<string>;
    typingUsers: Set<string>;
    loadConversations: () => Promise<void>;
    sendMessage: (recipientId: string, text: string, conversationId?: number) => Promise<void>;
    sendTypingStart: (recipientId: string, conversationId?: number) => void;
    sendTypingStop: (recipientId: string, conversationId?: number) => void;
    currentMessages: ChatMessagePayload[];
    setCurrentConversationId: (id: number | null) => void;
    currentConversationId: number | null;
    loadMessagesForConversation: (id: number) => Promise<void>;
    isChatDrawerOpen: boolean;
    toggleChatDrawer: () => void;
    cancelBookingRequest: (bookingId: number, conversationId: number, recipientId: string) => Promise<void>;
    proposeBooking: (bookingId: number, conversationId: number, recipientId: string, proposedDates: any[]) => Promise<void>;
    rejectBooking: (bookingId: number, conversationId: number, recipientId: string, reason: string) => Promise<void>;
    requestOtherTime: (bookingId: number, conversationId: number, recipientId: string) => Promise<void>;
    acceptBookingAndPay: (bookingId: number, conversationId: number, recipientId: string, acceptedDate: string, bookingUid: string) => Promise<string | undefined>;
    notifyScheduleRequest: (recipientId: string, conversationId: number, chatMessage?: any) => void;
    temporaryRecipient: {
        id: string;
        uid?: string;
        firstName: string;
        lastName: string;
        profilePicture: string | null;
    } | null;
    setTemporaryRecipient: (recipient: any) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, user } = useAuthContext();
    const chatSocket = useChatSocket();
    
    const [conversations, setConversations] = useState<ChatConversation[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
    const [currentMessages, setCurrentMessages] = useState<ChatMessagePayload[]>([]);
    const [isChatDrawerOpen, setIsChatDrawerOpen] = useState(false);
    const [temporaryRecipient, setTemporaryRecipient] = useState<any | null>(null);

    const currentConversationIdRef = useRef<number | null>(null);
    const conversationsRef = useRef<ChatConversation[]>([]);
    const isChatDrawerOpenRef = useRef<boolean>(false);
    const [chatActionToastPayload, setChatActionToastPayload] = useState<{title: string; body: string} | null>(null);

    useEffect(() => {
        currentConversationIdRef.current = currentConversationId;
    }, [currentConversationId]);

    useEffect(() => {
        isChatDrawerOpenRef.current = isChatDrawerOpen;
    }, [isChatDrawerOpen]);

    useEffect(() => {
        conversationsRef.current = conversations;
    }, [conversations]);

    const loadConversations = async () => {
        if (!isAuthenticated) return;
        try {
            const { data } = await axiosInstance.get('/api/chat/conversations');
            setConversations(data);
            
            // Calculate unread
            const unread = data.filter((c: ChatConversation) => c.lastMessage && !c.lastMessage.isRead && !c.lastMessage.isMine).length;
            setUnreadCount(unread);

            // Subscribe to presence for all conversational partners
            if (chatSocket.subscribePresence && data.length > 0) {
                const userIds = data.map((c: ChatConversation) => c.otherUser.id.toString());
                chatSocket.subscribePresence(userIds);
            }
        } catch (error) {
            console.error("Error loading conversations", error);
        }
    };

    const loadMessagesForConversation = async (id: number) => {
        try {
            // Fetch messages
            const { data } = await axiosInstance.get(`/api/chat/conversations/${id}/messages?limit=50`);
            setCurrentMessages(data.map((msg: any) => ({
                id: msg.id,
                senderId: msg.isMine ? 'me' : 'other', // Simplify for UI
                text: msg.text,
                timestamp: msg.createdAt,
                conversationId: id,
                messageType: msg.messageType,
                metadata: msg.metadata
            })));

            // Mark as read in backend
            const hasUnread = data.some((msg: any) => !msg.isMine && !msg.isRead);
            if (hasUnread) {
                await axiosInstance.patch(`/api/chat/conversations/${id}/read`);
                // Reload conversations to update the unread count badge in the UI
                loadConversations();
            }
        } catch (error) {
            console.error("Error loading messages", error);
        }
    };

    // Listen to incoming messages globally
    useEffect(() => {
        if (chatSocket.incomingMessage) {
            const message = chatSocket.incomingMessage;
            // Skip empty-text system notifications (they are only reload triggers)
            if (!message.text && message.messageType) {
                // Still need to reload messages if we're in that conversation
                if (isChatDrawerOpenRef.current && currentConversationIdRef.current === message.conversationId) {
                    loadMessagesForConversation(message.conversationId!);
                } else {
                    loadConversations();
                }
                return;
            }
            console.log("ChatContext: Received socket message:", message);
            
            // Check if it is an important action to display a toast
            const importantActions = ['schedule_request', 'proposal', 'payment_pending'];
            const importantEvents = ['cancel_request', 'request_rejected', 'expired', 'confirmed', 'request_other_time'];
            
            let isImportant = false;
            let actionTitle = "Actualización de Reserva";
            
            if (message.messageType && importantActions.includes(message.messageType)) {
                isImportant = true;
                if (message.messageType === 'schedule_request') actionTitle = 'Nueva Solicitud de Horario';
                else if (message.messageType === 'proposal') actionTitle = 'Nueva Propuesta de Horario';
                else if (message.messageType === 'payment_pending') actionTitle = 'Reserva Pendiente de Pago';
            } else if (message.messageType === 'system_event' && message.metadata?.event && importantEvents.includes(message.metadata.event)) {
                isImportant = true;
                if (message.metadata.event === 'cancel_request') actionTitle = 'Reserva Cancelada';
                else if (message.metadata.event === 'request_rejected') actionTitle = 'Solicitud Rechazada';
                else if (message.metadata.event === 'expired') actionTitle = 'Reserva Expirada';
                else if (message.metadata.event === 'confirmed') actionTitle = 'Reserva Confirmada';
                else if (message.metadata.event === 'request_other_time') actionTitle = 'Solicitud de Otro Horario';
            }
            
            // Only show toast if the message is from another user (not me)
            const isMineToast = message.senderId?.toString() === user?.uid?.toString() || message.senderId?.toString() === user?.id?.toString();
            if (isImportant && !isMineToast) {
                setChatActionToastPayload({
                    title: actionTitle,
                    body: message.text || "Tienes una nueva actualización importante en tus chats."
                });
            }
            
            let foundConversation = false;
            let senderIdFromConversation: string | number | null = null;

            setConversations(prev => {
                const updated = [...prev];
                const index = updated.findIndex(c => c.id === message.conversationId);
                
                if (index !== -1) {
                    foundConversation = true;
                    senderIdFromConversation = updated[index].otherUser.id;
                    const isMine = message.senderId?.toString() === user?.uid?.toString() || message.senderId?.toString() === user?.id?.toString();
                    updated[index].lastMessage = {
                        text: message.text,
                        createdAt: message.timestamp,
                        isRead: isMine,
                        isMine: isMine
                    };
                    updated[index].updatedAt = message.timestamp;
                    
                    // Move to top
                    const [moved] = updated.splice(index, 1);
                    updated.unshift(moved);
                }
                return updated;
            });

            // Need to use a slight timeout to let state update if we need to fetch
            setTimeout(() => {
                console.log("ChatContext: Processing message after timeout. foundConversation=", foundConversation);
                if (!foundConversation) {
                    console.log("ChatContext: New conversation! Fetching conversations...");
                    loadConversations();
                    setUnreadCount(prev => prev + 1);
                } else if (message.conversationId) {
                    const isCurrentConversationId = message.conversationId && Number(currentConversationIdRef.current) === Number(message.conversationId);
                    
                    let isSameUserDifferentConv = false;
                    if (!isCurrentConversationId && isChatDrawerOpenRef.current && currentConversationIdRef.current && senderIdFromConversation) {
                        const currentActiveConv = conversationsRef.current.find((c: ChatConversation) => c.id === currentConversationIdRef.current);
                        if (currentActiveConv && currentActiveConv.otherUser.id === senderIdFromConversation) {
                            isSameUserDifferentConv = true;
                        }
                    }

                    console.log("ChatContext: isCurrentConversationId:", isCurrentConversationId, "isSameUserDifferentConv:", isSameUserDifferentConv);

                    if (isCurrentConversationId || isSameUserDifferentConv) {
                        if (isSameUserDifferentConv) {
                            console.log("ChatContext: Switching active conversation from", currentConversationIdRef.current, "to", message.conversationId);
                            setCurrentConversationId(message.conversationId);
                        }

                        const isMine = message.senderId?.toString() === user?.uid?.toString() || message.senderId?.toString() === user?.id?.toString();
                        const newMsg: ChatMessagePayload = {
                            id: message.id,
                            senderId: isMine ? 'me' : 'other',
                            text: message.text,
                            timestamp: message.timestamp,
                            conversationId: message.conversationId,
                            messageType: message.messageType,
                            metadata: message.metadata
                        };
                        
                        setCurrentMessages(prev => {
                            if (message.id && prev.some(m => m.id === message.id)) return prev;
                            // Also avoid duplicate optimistic messages (which don't have an ID yet but have the same text and timestamp)
                            if (!message.id && prev.some(m => m.text === message.text && m.senderId === 'me' && Math.abs(new Date(m.timestamp).getTime() - new Date(message.timestamp).getTime()) < 5000)) return prev;
                            return [...prev, newMsg];
                        });
                        
                        if (isChatDrawerOpenRef.current || isSameUserDifferentConv) {
                            axiosInstance.patch(`/api/chat/conversations/${message.conversationId}/read`).catch(console.error);
                            console.log("ChatContext: Reloading messages for conversation:", message.conversationId);
                            loadMessagesForConversation(message.conversationId);
                        } else {
                            if (!isMine) {
                                console.log("ChatContext: Incrementing unread bubble because drawer is closed.");
                                setUnreadCount(prev => prev + 1);
                            }
                        }
                    } else {
                        const isMine = message.senderId?.toString() === user?.uid?.toString() || message.senderId?.toString() === user?.id?.toString();
                        if (!isMine) {
                            console.log("ChatContext: Incrementing unread bubble.");
                            setUnreadCount(prev => prev + 1);
                        }
                    }
                }
            }, 0);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatSocket.incomingMessage]);

    useEffect(() => {
        if (isAuthenticated) {
            loadConversations();
            
            // Bulletproof fallback: poll conversations every 15s
            const interval = setInterval(() => {
                loadConversations();
            }, 15000);
            
            return () => clearInterval(interval);
        }
    }, [isAuthenticated]);

    // Join socket rooms for all loaded conversations
    useEffect(() => {
        if (conversations.length > 0 && chatSocket.joinConversations) {
            const ids = conversations.map(c => c.id);
            chatSocket.joinConversations(ids);
        }
    }, [conversations, chatSocket.joinConversations]);

    const sendMessage = async (recipientId: string, text: string, conversationId?: number) => {
        try {
            // Optimistic UI
            const tempMsg: ChatMessagePayload = {
                senderId: 'me',
                text,
                timestamp: new Date().toISOString(),
                conversationId
            };
            if (conversationId === currentConversationId) {
                setCurrentMessages(prev => [...prev, tempMsg]);
            }

            // Save to Backend
            const { data } = await axiosInstance.post('/api/chat/messages', {
                recipientId,
                text,
                conversationId: conversationId && conversationId !== -1 ? conversationId : undefined
            });

            // Send via Socket
            chatSocket.sendMessage(recipientId, text, data.conversationId);
            
            // Reload conversations to move this one to the top
            loadConversations();
            
            // If this was a new conversation, update the current active id
            if ((!conversationId || conversationId === -1) && data.conversationId) {
                setCurrentConversationId(data.conversationId);
                setTemporaryRecipient(null);
            }
        } catch (error) {
            console.error("Error sending message", error);
        }
    };

    const cancelBookingRequest = async (bookingId: number, conversationId: number, recipientId: string) => {
        try {
            const { data } = await axiosInstance.post(`/api/chat/booking/${bookingId}/cancel`, {
                conversationId
            });
            
            if (data.chatMessage) {
                const newMsg: ChatMessagePayload = {
                    id: data.chatMessage.id,
                    senderId: 'me',
                    text: data.chatMessage.text,
                    timestamp: data.chatMessage.createdAt,
                    conversationId,
                    messageType: data.chatMessage.messageType,
                    metadata: data.chatMessage.metadata
                };
                
                if (conversationId === currentConversationId) {
                    setCurrentMessages(prev => [...prev, newMsg]);
                }
                
                // Emit so the other user sees the cancellation real-time
                // Notice that chatMessage doesn't go through standard socket send flow as easily because chatSocket.sendMessage only takes text.
                // However, the socket server broadcasts any message. Actually, socket.emit('send_message') doesn't support metadata directly in the hook.
                // For now, let's just trigger a reload of messages on the other side by emitting a normal message, or they'll get it when they poll.
                // The proper way would be for chatSocket to support sending complete message payloads. Let's rely on standard reload.
                chatSocket.sendSystemNotification(recipientId, conversationId, 'system_event', data.chatMessage.metadata);
                
                loadConversations();
            }
        } catch (error) {
            console.error("Error canceling booking", error);
        }
    };

    const proposeBooking = async (bookingId: number, conversationId: number, recipientId: string, proposedDates: any[]) => {
        try {
            const { data } = await axiosInstance.post(`/api/chat/booking/${bookingId}/propose`, {
                conversationId,
                proposedDates
            });
            if (data.chatMessage) {
                const newMsg: ChatMessagePayload = {
                    id: data.chatMessage.id,
                    senderId: 'me',
                    text: data.chatMessage.text,
                    timestamp: data.chatMessage.createdAt,
                    conversationId,
                    messageType: data.chatMessage.messageType,
                    metadata: data.chatMessage.metadata
                };
                if (conversationId === currentConversationId) {
                    setCurrentMessages(prev => [...prev, newMsg]);
                }
                chatSocket.sendSystemNotification(recipientId, conversationId, 'proposal', data.chatMessage.metadata);
                loadConversations();
            }
        } catch (error) {
            console.error("Error proposing booking", error);
            throw error;
        }
    };

    const requestOtherTime = async (bookingId: number, conversationId: number, recipientId: string) => {
        try {
            // Send the text message first
            await sendMessage(recipientId, "¿Podrías proponerme otro horario?", conversationId);
            
            // Send the system event
            const { data } = await axiosInstance.post('/api/chat/messages', {
                recipientId,
                text: "El paciente solicitó otro horario.",
                conversationId,
                messageType: 'system_event',
                metadata: {
                    bookingId,
                    event: 'request_other_time'
                }
            });

            // Optimistic UI for system event
            const newMsg: ChatMessagePayload = {
                id: data.id,
                senderId: 'me',
                text: data.text,
                timestamp: data.createdAt,
                conversationId,
                messageType: 'system_event',
                metadata: data.metadata
            };
            if (conversationId === currentConversationId) {
                setCurrentMessages(prev => [...prev, newMsg]);
            }
            chatSocket.sendSystemNotification(recipientId, conversationId, 'system_event', data.metadata);
            loadConversations();
        } catch (error) {
            console.error("Error requesting other time:", error);
            throw error;
        }
    };

    const rejectBooking = async (bookingId: number, conversationId: number, recipientId: string, reason: string) => {
        try {
            const { data } = await axiosInstance.post(`/api/chat/booking/${bookingId}/reject`, {
                conversationId,
                reason
            });
            if (data.chatMessage) {
                const newMsg: ChatMessagePayload = {
                    id: data.chatMessage.id,
                    senderId: 'me',
                    text: data.chatMessage.text,
                    timestamp: data.chatMessage.createdAt,
                    conversationId,
                    messageType: data.chatMessage.messageType,
                    metadata: data.chatMessage.metadata
                };
                if (conversationId === currentConversationId) {
                    setCurrentMessages(prev => [...prev, newMsg]);
                }
                chatSocket.sendSystemNotification(recipientId, conversationId, 'system_event', data.chatMessage.metadata);
                loadConversations();
            }
        } catch (error) {
            console.error("Error rejecting booking", error);
            throw error;
        }
    };

    const acceptBookingAndPay = async (bookingId: number, conversationId: number, recipientId: string, acceptedDate: string, bookingUid: string): Promise<string | undefined> => {
        try {
            // First accept the booking to transition to WAITING_PAYMENT
            const { data } = await axiosInstance.post(`/api/chat/booking/${bookingId}/accept`, {
                conversationId,
                acceptedDate
            });
            if (data.chatMessage) {
                const newMsg: ChatMessagePayload = {
                    id: data.chatMessage.id,
                    senderId: 'me',
                    text: data.chatMessage.text,
                    timestamp: data.chatMessage.createdAt,
                    conversationId,
                    messageType: data.chatMessage.messageType,
                    metadata: data.chatMessage.metadata
                };
                if (conversationId === currentConversationId) {
                    setCurrentMessages(prev => [...prev, newMsg]);
                }
                chatSocket.sendMessage(recipientId, "Horario aceptado. Pendiente de pago.", conversationId);
                loadConversations();
            }

            // Now call the payment API to get the Plexo URL
            const payRes = await axiosInstance.post(`/api/bookings/${bookingUid}/payments`, {
                amount: data.chatMessage?.metadata?.consultPrice || 3000,
                currency: 'UYU',
                paymentMethod: 'plexo'
            });
            
            return payRes.data?.payment_url;
        } catch (error) {
            console.error("Error accepting booking and fetching payment URL", error);
            return undefined;
        }
    };

    const notifyScheduleRequest = (recipientId: string, conversationId: number, chatMessage?: any) => {
        if (chatMessage) {
            // Optimistic UI for sender
            const newMsg: ChatMessagePayload = {
                id: chatMessage.id,
                senderId: 'me',
                text: chatMessage.text,
                timestamp: chatMessage.createdAt,
                conversationId: conversationId,
                messageType: chatMessage.messageType,
                metadata: chatMessage.metadata
            };
            if (conversationId === currentConversationIdRef.current || conversationId === currentConversationId) {
                setCurrentMessages(prev => [...prev, newMsg]);
            }

            if (chatSocket.socket && chatSocket.isConnected) {
                chatSocket.socket.emit('send_message', { 
                    recipientId, 
                    text: chatMessage.text, 
                    conversationId, 
                    messageType: chatMessage.messageType,
                    metadata: chatMessage.metadata
                });
            }
        } else {
            chatSocket.sendSystemNotification(recipientId, conversationId, 'system_event', chatMessage?.metadata);
        }
    };

    const toggleChatDrawer = () => setIsChatDrawerOpen(prev => !prev);

    return (
        <ChatContext.Provider value={{
            conversations,
            unreadCount,
            socketConnected: chatSocket.isConnected,
            onlineUsers: chatSocket.onlineUsers,
            typingUsers: chatSocket.typingUsers,
            loadConversations,
            sendMessage,
            sendTypingStart: chatSocket.sendTypingStart,
            sendTypingStop: chatSocket.sendTypingStop,
            currentMessages,
            setCurrentConversationId,
            currentConversationId,
            loadMessagesForConversation,
            isChatDrawerOpen,
            toggleChatDrawer,
            cancelBookingRequest,
            proposeBooking,
            rejectBooking,
            requestOtherTime,
            acceptBookingAndPay,
            notifyScheduleRequest,
            temporaryRecipient,
            setTemporaryRecipient
        }}>
            {children}
            <ChatActionToast 
                payload={chatActionToastPayload} 
                onClose={() => setChatActionToastPayload(null)} 
            />
        </ChatContext.Provider>
    );
};

export const useChat = () => {
    const context = useContext(ChatContext);
    if (context === undefined) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
};
